import ImageComponent from '../../atoms/image-component/image-component';
import RichText from '../../atoms/rich-text/rich-text';
import styles from './card.module.scss';
import IRichText from '../../../models/viewModels/base/IRichText';
import IButton from '../../../models/button/IButton';
import Button from '../../atoms/button/button';
import IImage from '../../../models/viewModels/base/IImage';
import colorStyle from '../../../styles/utils/variables.module.scss';
import { ButtonStyleType } from '../../../constants/button-style-type';
import { ApplicationSettings } from '../../../models/settings';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';

export type CardProps = {
  orientation?: string;
  aspectRatio?: string;
  redirectToUrl?: string;
  image?: IImage;
  richText?: IRichText;
  secondaryText?: IRichText;
  buttonProp?: IButton;
  appSettings?: ApplicationSettings;
};

export type CardWrapperProps = {
  children: React.ReactNode;
  cardUrl?: string;
  buttonUrl?: string;
  className: string;
  style: any;
};

const isClient = typeof window !== 'undefined';

const CardWrapper = ({ children, className, style, cardUrl, buttonUrl }: CardWrapperProps) => {
  return cardUrl && !buttonUrl ? (
    <Link className={className} style={style} href={cardUrl}>
      {children}
    </Link>
  ) : (
    <div className={className} style={style}>
      {children}
    </div>
  );
};

export default function Card({
  orientation,
  aspectRatio,
  redirectToUrl,
  image,
  richText,
  secondaryText,
  buttonProp,
  appSettings,
}: CardProps) {
  if (image === null && richText === null && secondaryText === null && buttonProp === null) {
    return null;
  }

  return (
    <CardWrapper
      className={styles[`card`]}
      cardUrl={redirectToUrl}
      buttonUrl={buttonProp?.buttonURL}
      style={{ cursor: redirectToUrl ? 'pointer' : 'default' }}
    >
      <div
        className={`${
          orientation != 'horizontal' ? styles[`vertical-card`] : styles[`horizontal-card`]
        } ${aspectRatio == 'keep ratio' ? styles[`keep-ratio`] : styles[`fill-height`]}`}
      >
        {image && (
          <div className={styles[`media`]}>
            <ImageComponent
              src={image.secureUrl}
              altText={image.alt}
              width={+image.width}
              height={+image.height}
            />
          </div>
        )}
        <div
          className={`${styles[`text-container`]} ${
            image == null && styles[`text-container-without-image`]
          }`}
        >
          {!!richText && isClient && (
            <div className={styles[`rich-text-block`]}>
              <RichText richText={richText}></RichText>
            </div>
          )}
          {!!secondaryText && isClient && (
            <div
              className={`${styles[`text-block`]} ${
                buttonProp == null && styles[`text-block-without-button`]
              }`}
            >
              <RichText richText={secondaryText}></RichText>
            </div>
          )}
          {!!buttonProp && (
            <div className={styles[`button-block`]}>
              <Button
                buttonID={buttonProp.buttonID}
                buttonURL={redirectToUrl ? '' : buttonProp.buttonURL}
                buttonNewTab={buttonProp.buttonNewTab}
                buttonAriaLabel={buttonProp.buttonAriaLabel}
                buttonRole={buttonProp.buttonRole}
                buttonName={buttonProp.buttonName}
                buttonSize={buttonProp.buttonSize}
                buttonStyle={buttonProp.buttonStyle}
                copyColor={
                  buttonProp.buttonStyle === ButtonStyleType.Outline
                    ? appSettings.branding.primaryColor
                    : colorStyle.white
                }
                backgroundColor={
                  buttonProp.buttonStyle === ButtonStyleType.Outline
                    ? colorStyle.white
                    : appSettings.branding.primaryColor
                }
                outlineColor={appSettings.branding.primaryColor}
                hoverBackgroundColor={appSettings.branding.primaryHoverColor}
                activeBackgroundColor={appSettings.branding.primaryActiveColor}
                heapIdentifier={buttonProp.buttonID}
                buttonType={buttonProp.buttonType}
              >
                {buttonProp.children}
              </Button>
            </div>
          )}
        </div>
      </div>
    </CardWrapper>
  );
}
